/**
 * Captain Bram, the guide: the shared contract for the onboarding, tutorial and hint system (/tmp/tutorial/DESIGN.md).
 *
 * One GuideController (WP-A, ui/guide/guideController.ts) owns everything that teaches: the welcome, the level-1
 * walkthrough, the contextual hints and the hub's first-session coaching. Scripts (WP-B levels, WP-C hub) are
 * declarative GuideSteps. The screens feed the controller facts and signals and give it a host to draw on.
 *
 * This file holds the guide's switch, its save shape and its create options, and re-exports the shared types
 * (guideTypes.ts, guideFacts.ts) and text helpers (guideText.ts), so every importer keeps using "./guide". None of
 * them touches the DOM at module load, so node tests (vitest, environment "node") can use them. Everything here is
 * frozen once the coordinator commits it: a change goes through the coordinator.
 */
import type { GuideApi } from "./guideTypes";

export * from "./guideTypes";
export * from "./guideFacts";
export * from "./guideText";

// ------------------------------------------------------------------------------------------------ the character

export const GUIDE_NAME = "Captain Bram";

// ------------------------------------------------------------------------------------------------ the installed guide

let current: GuideApi | null = null;
/** main.ts (WP-C) installs the controller at boot, or null with ?noguide. */
export function setGuide(g: GuideApi | null): void { current = g; }
/** The installed guide, or null (no guide: every screen behaves as before, with no tutorial). */
export function guide(): GuideApi | null { return current; }

// ------------------------------------------------------------------------------------------------ save

/** SaveRecord.guide (packages/app/src/saves.ts carries the same shape as `guide?: GuideSaveJson`). */
export interface GuideSaveJson {
  v: number;
  /** Step ids done: finished, dismissed, or migrated as already known. */
  done: string[];
  tipsOff?: boolean;
  walkthroughSkipped?: boolean;
}
export const GUIDE_SAVE_VERSION = 1;

/** The legacy fields the migration reads (SaveRecord, before the guide). */
export interface GuideLegacySave {
  readonly highestLevelCleared: number;
  /** ui/tutorial.ts step reached; −1 = skipped or finished. */
  readonly tutorialStep?: number;
  /** ui/hub/coach.ts COACH_STEPS index reached. */
  readonly coachStep?: number;
  readonly gearGateSeen?: boolean;
}

/** Everything createGuide (WP-A, ui/guide/guideController.ts) needs; main.ts (WP-C) builds it at boot. */
export interface GuideCreateOptions {
  readonly saved: GuideSaveJson | undefined;
  readonly legacy: GuideLegacySave;
  /** Unlock levels from balance, so a migrated player is not taught what they have already met. */
  readonly unlocks: {
    readonly enemies: readonly { readonly id: string; readonly unlockLevel: number; readonly air: boolean }[];
    readonly towers: readonly { readonly id: string; readonly unlockLevel: number }[];
  };
  /** Write the guide's save (main.ts stores it in SaveRecord.guide and saves). */
  persist(save: GuideSaveJson): void;
  /** ms clock (performance.now in the app; a fake in tests). */
  now(): number;
}
export type CreateGuide = (o: GuideCreateOptions) => GuideApi;

// ------------------------------------------------------------------------------------------------ switches

/** `?noguide` turns the whole guide off (smoke tests, captures). */
export const GUIDE_OFF_PARAM = "noguide";
export function guideOffByUrl(search: string): boolean { return new URLSearchParams(search).has(GUIDE_OFF_PARAM); }
